import React, { useState, useCallback, useEffect, useContext, useRef } from 'react';
import { useMatch, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber } from 'react-phone-number-input';
import { validateAddEditForm } from './validations';
import Tabs from './components/Tabs';
import UserDetailsTab from './components/UserDetailsTab';
import Header from './components/Header';
import Buttons from './components/Buttons';
import useFetch from '../../../hooks/useFetch';
import { AuthContext } from '../../../contexts/AuthContext';
import {
  VALIDATION_FIELD_MAPPING,
  APP_SUCCESS_MESSAGES,
} from '../../../utils/appConstant';
import { AddEdit as AddEditInterface } from '../../../interfaces/user.interface';
import { Validation } from '../../../interfaces/common';
import UserService from '../../../services/user.service';
import RoleService from '../../../services/role.service';
import CommonService from '../../../services/common.service';
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../enums';
import Spinner from '../../../components/Spinner';
import Toast from '../../../components/Toast';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import { STATUS_CODE } from '../../../utils/apiConstant';

const initialFormData: AddEditInterface = {
  firstName: '',
  lastName: '',
  displayName: '',
  email: '',
  phone: '',
  countryCode: '+91',
  profileImage: '',
  profileImageFile: null,
  role: '',
  status: true,
  cityId: [],
  stateId: '',
  countryId: '',
  addressLine1: '',
  addressLine2: '',
  pincode: '',
  area: [],
  reraId: '',
  notes: '',
  companyName: '',
  companyContactNumber: '',
  companyCountryCode: '+91',
  companyLogo: '',
  companyLogoFile: null,
  companyAddressLine1: '',
  companyAddressLine2: '',
  companyCityId: '',
  companyStateId: '',
  companyCountryId: '',
  companyPincode: '',
};

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  role: '',
  city: '',
  state: '',
  country: '',
  companyCity: '',
  companyState: '',
  companyCountry: '',
  area: '',
};

const AddEditView: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();
  const idRef = useRef(id);

  useEffect(() => {
    idRef.current = id;
  }, [id]);

  const isActiveParam = !id ? 1 : undefined;

  const view = !!useMatch(Modules.users + AuthRoutes.users + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.users + AuthRoutes.users + RouteParamsTypes.edit + '/:id');
  // ... (keep existing state)
  const navigate = useNavigate();
  const { loggedInUser, setLoggedInUser } = useContext(AuthContext);

  const [tab, setTab] = useState<'userDetails'>('userDetails');
  const [formData, setFormData] = useState<AddEditInterface>(initialFormData);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [loading, setLoading] = useState(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [_counter, _setCounter] = useState<number>(0);
  const [validation, setValidation] = useState<Validation>(initialValidation);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; type: string | null }>({
    open: false,
    type: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleCloseDelete = () => {
    setDeleteConfirm({ open: false, type: null });
  };

  const handleRemoveProfileImage = () => {
    if (formData.profileImageFile) {
        setFormData((prev) => ({ ...prev, profileImageFile: null }));
        return;
    }
    if (id && formData.profileImage) {
      setDeleteConfirm({ open: true, type: 'profile_image' });
    }
  };

  const handleRemoveCompanyLogo = () => {
    if (formData.companyLogoFile) {
        setFormData((prev) => ({ ...prev, companyLogoFile: null }));
        return;
    }
    if (id && formData.companyLogo) {
      setDeleteConfirm({ open: true, type: 'company_logo' });
    }
  };

  const handleConfirmDelete = async () => {
    if (!id || !deleteConfirm.type) return;

    setDeleteLoading(true);
    try {
      if (deleteConfirm.type === 'profile_image') {
        await UserService.deleteProfileImage(id);
        setFormData((prev) => ({ ...prev, profileImage: '', profileImageFile: null }));
        toast.success(<Toast message={t('common.messages.fileDeleted')} />, { icon: false });
      } else if (deleteConfirm.type === 'company_logo') {
        await UserService.deleteCompanyLogo(id);
        setFormData((prev) => ({ ...prev, companyLogo: '', companyLogoFile: null }));
        toast.success(<Toast message={t('common.messages.fileDeleted')} />, { icon: false });
      }
      handleCloseDelete();
    } catch (error) {
      console.error(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Lookup Hooks
  const { data: allRolesLookup } = useFetch({
    service: RoleService,
    func: 'lookup',
    payload: { searchText: '', is_active: isActiveParam },
    dependency: [isActiveParam],
  });

  const { data: roles } = useFetch({
    service: RoleService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.role || '', is_active: isActiveParam },
    dependency: [dropdownSearchData.role, isActiveParam],
  });

  const { data: userCountries } = useFetch({
    service: CommonService,
    func: 'countriesLookup',
    payload: { searchText: dropdownSearchData.country || '' },
    dependency: [dropdownSearchData.country],
  });

  const { data: userStates } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: {
      searchText: dropdownSearchData.state || '',
      countryId: formData.countryId,
    },
    dependency: [dropdownSearchData.state, formData.countryId],
  });

  const { data: userCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: {
      searchText: dropdownSearchData.city || '',
      stateId: formData.stateId,
    },
    dependency: [dropdownSearchData.city, formData.stateId],
  });

  const { data: companyCountries } = useFetch({
    service: CommonService,
    func: 'countriesLookup',
    payload: { searchText: dropdownSearchData.companyCountry || '' },
    dependency: [dropdownSearchData.companyCountry],
  });

  const { data: companyStates } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: {
      searchText: dropdownSearchData.companyState || '',
      countryId: formData.companyCountryId,
    },
    dependency: [dropdownSearchData.companyState, formData.companyCountryId],
  });

  const { data: companyCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: {
      searchText: dropdownSearchData.companyCity || '',
      stateId: formData.companyStateId,
    },
    dependency: [dropdownSearchData.companyCity, formData.companyStateId],
  });

  const { data: areas } = useFetch({
    service: CommonService,
    func: 'areaLookup',
    payload: {
      searchText: dropdownSearchData.area || '',
      cityId: formData.cityId,
      is_active: isActiveParam
    },
    dependency: [dropdownSearchData.area, JSON.stringify(formData.cityId), isActiveParam],
    onSuccess: (response: any) => {
      const fetchedAreas = Array.isArray(response) ? response : (response?.records || []);
      setFormData((prev) => {
        if (!prev.area || prev.area.length === 0) return prev;
        const validAreaIds = new Set(fetchedAreas.map((a: any) => String(a.id)));
        const filteredAreas = prev.area.filter((areaId: string) => validAreaIds.has(areaId));
        if (filteredAreas.length !== prev.area.length) {
          return { ...prev, area: filteredAreas };
        }
        return prev;
      });
    }
  });

  useEffect(() => {
    if (!id && userCountries?.length > 0 && !formData.countryId) {
      const india = userCountries.find(
        (country: any) => country.name.toLowerCase() === 'india'
      );
      if (india) {
        setFormData((prev) => ({
          ...prev,
          countryId: india.id,
        }));
      }
    }
  }, [userCountries, id, formData.countryId]);

  useEffect(() => {
    if (!id && userStates?.length > 0 && !formData.stateId) {
      const gujarat = userStates.find(
        (state: any) => state.name.toLowerCase() === 'gujarat'
      );
      if (gujarat) {
        setFormData((prev) => ({
          ...prev,
          stateId: gujarat.id,
        }));
      }
    }
  }, [userStates, id, formData.stateId]);

  useEffect(() => {
    setFormData(initialFormData);
    setDropdownSearchData(initialDropdownSearchData);
    setValidation(initialValidation);
  }, [location.key]);

  const { loading: getContentLoading } = useFetch({
    service: UserService,
    func: 'get',
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: async (response: any) => {
      if (idRef.current !== id) return;
      setFormData({
        firstName: response.first_name || '',
        lastName: response.last_name || '',
        displayName: response.display_name || '',
        email: response.email || '',
        role: response.role_id || '',
        phone: response.phone || response.phone_number || '', /* Fallback safely */
        countryCode: response.country_code || '+91',
        profileImage: response.profile_image_url || '',
        profileImageFile: null,
        cityId: response.city_ids ? response.city_ids.map(String) : (response.city_id ? [String(response.city_id)] : []),
        stateId: response.state_id || '',
        countryId: response.country_id || '',
        addressLine1: response.address_1 || '',
        addressLine2: response.address_2 || '',
        pincode: response.pincode || '',
        area: response.area_user_ids ? response.area_user_ids.map((id: number) => String(id)) : [],
        reraId: response.rera_id || '',
        notes: response.notes || '',
        status: response.is_active,
        companyName: response.company_details?.company_name || '',
        companyContactNumber: response.company_details?.company_contact_number || '',
        companyLogo: response.company_details?.company_logo_url || '',
        companyLogoFile: null,
        companyAddressLine1: response.company_details?.company_address_1 || '',
        companyAddressLine2: response.company_details?.company_address_2 || '',
        companyCityId: response.company_details?.company_city_id || '',
        companyStateId: response.company_details?.company_state_id || '',
        companyCountryId: response.company_details?.company_country_id || '',
        companyPincode: response.company_details?.company_pincode || '',
      });
    },
    onError: (error: any) => {
      if (error.code === STATUS_CODE.notFound) navigate(Modules.users + AuthRoutes.users);
    },
  });

  const handleTabChange = useCallback((e: any, value: 'userDetails') => {
    setTab(value);
  }, []);

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      if (name === VALIDATION_FIELD_MAPPING.phone) {
        const phoneNumber = parsePhoneNumber(event?.target?.value ?? '');

        setFormData((prev) => {
          return {
            ...prev,
            [VALIDATION_FIELD_MAPPING.phone]: phoneNumber && phoneNumber.nationalNumber,
            [VALIDATION_FIELD_MAPPING.countryCode]: phoneNumber
              ? `+${phoneNumber && phoneNumber.countryCallingCode}`
              : '',
          };
        });
      } else if (name === VALIDATION_FIELD_MAPPING.companyContactNumber) {
        const phoneNumber = parsePhoneNumber(event?.target?.value ?? '');

        setFormData((prev) => {
          return {
            ...prev,
            [VALIDATION_FIELD_MAPPING.companyContactNumber]:
              phoneNumber && phoneNumber.nationalNumber,
            [VALIDATION_FIELD_MAPPING.companyCountryCode]: phoneNumber
              ? `+${phoneNumber && phoneNumber.countryCallingCode}`
              : '',
          };
        });
      } else {
        let value: any;

        if (name === VALIDATION_FIELD_MAPPING.status) {
          value = event.target.checked;
        } else if (name === VALIDATION_FIELD_MAPPING.profileImageFile) {
          // Handle file input
          value = event.target.value;
        } else {
          value = event.target.value;
        }

        setFormData((prev) => {
          const newData: any = {
            ...prev,
            [name]: value,
          };

          // Clear city when state changes (for both user and company)
          if (name === VALIDATION_FIELD_MAPPING.stateId) {
            newData[VALIDATION_FIELD_MAPPING.cityId] = [];
            newData[VALIDATION_FIELD_MAPPING.area] = [];
          }
          if (name === VALIDATION_FIELD_MAPPING.companyStateId) {
            newData[VALIDATION_FIELD_MAPPING.companyCityId] = '';
          }

          // Clear state and city when country changes
          if (name === VALIDATION_FIELD_MAPPING.countryId) {
            newData[VALIDATION_FIELD_MAPPING.stateId] = '';
            newData[VALIDATION_FIELD_MAPPING.cityId] = [];
            newData[VALIDATION_FIELD_MAPPING.area] = [];
          }
          if (name === VALIDATION_FIELD_MAPPING.companyCountryId) {
            newData[VALIDATION_FIELD_MAPPING.companyStateId] = '';
            newData[VALIDATION_FIELD_MAPPING.companyCityId] = '';
          }

          return newData;
        });
      }
    },
    [setFormData]
  );

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      let key = name;
      if (name === VALIDATION_FIELD_MAPPING.countryId) key = 'country';
      else if (name === VALIDATION_FIELD_MAPPING.stateId) key = 'state';
      else if (name === VALIDATION_FIELD_MAPPING.cityId) key = 'city';
      else if (name === VALIDATION_FIELD_MAPPING.companyCountryId) key = 'companyCountry';
      else if (name === VALIDATION_FIELD_MAPPING.companyStateId) key = 'companyState';
      else if (name === VALIDATION_FIELD_MAPPING.companyCityId) key = 'companyCity';

      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [key]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      const value = event.target.value;
      const finalValue = typeof value === 'string' ? value.trim() : value;

      let newValidation = validateAddEditForm(
        {
          ...formData,
          [name]: finalValue,
        },
        name
      );
      setValidation(newValidation);
    },
    [formData]
  );

  const handleSubmit = useCallback(
    async (action: '1' | '2', event?: React.FormEvent) => {
      event && event.preventDefault();

      const newValidation = validateAddEditForm(formData);
      setValidation(newValidation);
      if (!newValidation.validate) return;

      if (action === '1') setLoading(true);
      else setActionTwoLoading(true);

      try {
        let response: any;

        const fd = new FormData();
        fd.append('first_name', formData.firstName.trim());
        fd.append('last_name', formData.lastName.trim());
        fd.append('display_name', formData.displayName?.trim() || '');
        fd.append('email', formData.email?.trim() || '');
        fd.append('role_id', formData.role || '');
        fd.append('phone', (formData.phone && formData.phone.trim()) || '');
        fd.append('country_code', (formData.countryCode && formData.countryCode.trim()) || '');

        if (formData.profileImageFile) {
          fd.append('profile_image', formData.profileImageFile);
        }

        fd.append('is_active', formData.status ? 'true' : 'false');
        if (formData.cityId && formData.cityId.length) {
          formData.cityId.forEach((val: string) => fd.append('city_ids[]', val));
        }
        fd.append('state_id', formData.stateId || '');
        fd.append('country_id', formData.countryId || '');
        fd.append('address_1', formData.addressLine1?.trim() || '');
        fd.append('address_2', formData.addressLine2?.trim() || '');
        fd.append('pincode', formData.pincode?.trim() || '');
        fd.append('rera_id', formData.reraId?.trim() || '');
        fd.append('notes', formData.notes?.trim() || '');
        fd.append('company_name', formData.companyName?.trim() || '');
        fd.append('company_contact_number', formData.companyContactNumber?.trim() || '');

        if (formData.companyLogoFile) {
          fd.append('company_logo', formData.companyLogoFile);
        }

        fd.append('company_address_1', formData.companyAddressLine1?.trim() || '');
        fd.append('company_address_2', formData.companyAddressLine2?.trim() || '');
        fd.append('company_city_id', formData.companyCityId || '');
        fd.append('company_state_id', formData.companyStateId || '');
        fd.append('company_country_id', formData.companyCountryId || '');
        fd.append('company_pincode', formData.companyPincode?.trim() || '');

        if (formData.area && formData.area.length) {
          formData.area.forEach((val: string) => fd.append('area_user_ids[]', val));
        }

        if (edit && id) {
          response = await UserService.edit(id, fd);
        } else {
          response = await UserService.add(fd);
        }

        if (response.success !== false) {
          toast.success(
            <Toast
              message={edit ? APP_SUCCESS_MESSAGES.userUpdated : APP_SUCCESS_MESSAGES.userCreated}
            />,
            { icon: false }
          );

          if (edit) {
            if (loggedInUser && String(loggedInUser.id) === String(id)) {
              const updatedUser = await UserService.get(id as string);
              setLoggedInUser(updatedUser);
            }
            navigate(Modules.users + AuthRoutes.users);
          } else if (action === '2') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const input = document.getElementById(VALIDATION_FIELD_MAPPING.firstName);
            input && input.focus();
            navigate(Modules.users + AuthRoutes.users + RouteParamsTypes.add, {
              replace: true,
            });
          } else {
            const _createdId = response?.id;
            navigate(`${Modules.users}${AuthRoutes.users}`, { replace: true });
          }
        }

        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      } catch (err: any) {
        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, edit, formData, navigate]
  );

  const handleKeyDown = useCallback(
    (e: any, name?: string) => {
      if (name === VALIDATION_FIELD_MAPPING.notes) return;
      if (e.key === 'Enter') handleSubmit('1');
    },
    [handleSubmit]
  );

  const handleCancel = () => {
    navigate(Modules.users + AuthRoutes.users);
  };

  let formContent;

  if (getContentLoading) {
    formContent = (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  } else if (tab === 'userDetails') {
    formContent = (
      <UserDetailsTab
        edit={edit}
        view={view}
        allRoles={allRolesLookup}
        formData={formData}
        roles={roles}
        validation={validation}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onKeyDown={handleKeyDown}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
        userCountries={userCountries}
        userStates={userStates}
        userCities={userCities}
        companyCountries={companyCountries}
        companyStates={companyStates}
        companyCities={companyCities}
        areas={areas}
        allAreas={areas}
        onRemoveProfileImage={handleRemoveProfileImage}
        onRemoveCompanyLogo={handleRemoveCompanyLogo}
      />
    );
  }

  return (
    <>
      <Header id={id} view={view} edit={edit} />

      <div className="card mb-6">
        <h5 className="card-header">
          {!id
            ? t('users.addEdit.pageTexts.title.addTitle')
            : view
              ? t('users.addEdit.pageTexts.title.viewTitle')
              : t('users.addEdit.pageTexts.title.editTitle')}
        </h5>

        <hr className="m-0"></hr>

        <Tabs tab={tab} add={!edit && !view} onTabChange={handleTabChange} />

        <hr className="m-0"></hr>

        {formContent}

        <hr className="m-0"></hr>

        <Buttons
          tab={tab}
          actionTwoLoading={actionTwoLoading}
          loading={loading}
          view={view}
          edit={edit}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />

        <Modal
          open={deleteConfirm.open}
          onClose={handleCloseDelete}
          title={t('common.modal.deleteConfirmationTitle')}
          maxWidth="sm"
          content={
            <div className="text-center">
              <i className="ri-error-warning-line text-warning fs-1 mb-3"></i>
              <p className="mb-0">{t('common.modal.deleteConfirmationMessage')}</p>
            </div>
          }
          action={
            <div className="d-flex justify-content-center gap-3 w-100 pb-3">
              <Button
                className="btn btn-outline-secondary"
                onClick={handleCloseDelete}
                text={t('common.buttons.cancel')}
                disabled={deleteLoading}
              />
              <Button
                className="btn btn-danger"
                onClick={handleConfirmDelete}
                text={t('common.buttons.delete')}
                loading={deleteLoading}
                disabled={deleteLoading}
              />
            </div>
          }
        />
      </div>
    </>
  );
};

export default AddEditView;
